import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { cloneDeep } from 'lodash-es';
import { Fragment, type JSX, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { generatePath } from 'react-router';

import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { ConfirmationModal } from 'Components/Modal/ConfirmationModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { LinkButtonToast, TextToast } from 'Components/Toast/Toast';
import { ICON_DELETE_REMOVE, ICON_SAVE } from 'Config/Icons';
import { THIRD_PARTIES_AND_SERVICES_LISTING } from 'Config/PathTemplates';
import { TPRM_THIRD_PARTY_SERVICE_DELEGATES, TPRM_THIRD_PARTY_SERVICE_DESCRIPTION, TPRM_THIRD_PARTY_SERVICE_MANAGER, TPRM_THIRD_PARTY_SERVICE_NAME, TPRM_THIRD_PARTY_SERVICE_RESPONSIBLE_ORGANIZATION } from 'Config/Tooltips';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { validateEmail } from 'Helpers/InputValidation';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { ValidationError } from 'Models/ErrorTypes';
import { CreateServiceRequest, FinalizationState, ScheduleV2, ServiceForNonTprmUser, ThirdPartyContact, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser, UpdateServiceRequest } from 'Models/TPRM';
import { GroupOptionType, OptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

import styles from './ManageThirdPartyServiceForm.module.css';
import { FinalizeThirdPartyServiceModal } from '../../../Components/FinalizeThirdPartyServiceModal/FinalizeThirdPartyServiceModal';

export type ManageThirdPartyServiceFormProps =
    | {
          serviceExists: false;
          userHasFullTprmAccess: false;

          thirdParties: ThirdPartyWithServicesForNonTprmUser[];
          tprmApi: ClientTPRMApi;

          service?: never;
      }
    | {
          serviceExists: true;
          userHasFullTprmAccess: false;

          service: ServiceForNonTprmUser;
          thirdParties: ThirdPartyWithServicesForNonTprmUser[];
          tprmApi: ClientTPRMApi;
      }
    | {
          serviceExists: false;
          userHasFullTprmAccess: true;

          thirdParties: ThirdPartyResponseWithServices[];
          tprmApi: ClientTPRMApi;

          irqTargetCompletionSetting?: ScheduleV2;
          service?: never;
      }
    | {
          serviceExists: true;
          userHasFullTprmAccess: true;

          service: ThirdPartyService;
          thirdParties: ThirdPartyResponseWithServices[];
          tprmApi: ClientTPRMApi;

          irqTargetCompletionSetting?: ScheduleV2;
      };

interface FormFieldsState {
    thirdPartyId?: string;
    name?: string;
    description?: string;
    thirdPartyServiceManagerUserId?: string;
    delegates?: UserResponse[];
    third_party_contacts: ThirdPartyContact[];
    responsibleOrganization?: string;
}

type RequestActionNoConfirmationNeeded = 'edit' | 'createAsDraft' | 'createAndSubmit';
type RequestActionConfirmationNeeded = 'createAndFinalize' | 'delete';

type RequestAndModalState =
    | {
          requestState: 'idle';
          displayedModal?: 'confirmFinalizeNewService' | 'confirmDelete';
      }
    | {
          requestState: 'inProgress';
          action: RequestActionNoConfirmationNeeded | RequestActionConfirmationNeeded;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionNoConfirmationNeeded;
          showToast: boolean;
          displayedModal?: never;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionConfirmationNeeded;
          displayedModal?: 'confirmFinalizeNewService' | 'confirmDelete';
      }
    | {
          requestState: 'failure';
          errorMessage: string;
          showToast: boolean;
      };

/**
 * Renders a form for creating a new third-party service, or updating or deleting an existing third-party service.
 */
export const ManageThirdPartyServiceForm = (props: ManageThirdPartyServiceFormProps): JSX.Element => {
    const cachedData = useCachedData();

    const [requestAndModalState, setRequestAndModalState] = useState<RequestAndModalState>({ requestState: 'idle' });
    const [serviceManager, setServiceManager] = useState<UserResponse | undefined>(cachedData.users.find((user) => user.cognito_subject === props.service?.vendor_service_manager_user_id)); // Keeps track of the currently selected User in the Third-Party Manager drop-down menu.

    // TODO: Extract these form fields into their own states.
    const [formFieldsState, setFormFieldsState] = useState<FormFieldsState>({
        thirdPartyId: props.service?.vendor_id,
        name: props.service?.name,
        description: props.service?.description,
        thirdPartyServiceManagerUserId: props.service?.vendor_service_manager_user_id,
        delegates: props.service?.delegates ? cachedData.users.filter((user) => props.service?.delegates?.includes(user.cognito_subject)) : undefined,
        third_party_contacts: props.service?.vendor_contacts || [],
        responsibleOrganization: props.service?.responsible_organization,
    });
    const [tags, setTags] = useState<string[]>(props.service ? props.service.tags : []);

    const disabled = requestAndModalState.requestState === 'inProgress' || requestAndModalState.requestState === 'success';

    const selectedThirdParty = useMemo(() => {
        return props.userHasFullTprmAccess ? props.thirdParties?.find((thirdParty) => thirdParty.id === formFieldsState.thirdPartyId) : undefined;
    }, [formFieldsState.thirdPartyId, props.thirdParties, props.userHasFullTprmAccess]);

    const createThirdPartyOptions = (): OptionType[] => {
        const thirdPartyOptions: OptionType[] = [];
        props.thirdParties?.forEach((thirdParty) => {
            thirdPartyOptions.push({
                label: thirdParty.name,
                value: thirdParty.id,
            });
        });
        thirdPartyOptions.sort((a, b) => a.label.localeCompare(b.label));
        return thirdPartyOptions;
    };

    const createThirdPartyService = async (finalizationState: FinalizationState, action: 'createAsDraft' | 'createAndSubmit', irqTargetCompletionDate?: Date): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: action });

        try {
            validateForm();
            await props.tprmApi.createThirdPartyService(buildCreateRequest(finalizationState, irqTargetCompletionDate), formFieldsState.thirdPartyId!);
            setRequestAndModalState({ requestState: 'success', completedAction: action, showToast: true });
        } catch (err) {
            handleRequestError(err);
        }
    };

    const updateThirdPartyService = async (): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'edit' });

        try {
            validateForm();
            await props.tprmApi.updateThirdPartyService(buildUpdateRequest(), formFieldsState.thirdPartyId!, props.service!.id);
            setRequestAndModalState({ requestState: 'success', completedAction: 'edit', showToast: true });
        } catch (err) {
            handleRequestError(err);
        }
    };

    const buildCreateRequest = (finalizationState: FinalizationState, irqTargetCompletionDate?: Date): CreateServiceRequest => {
        const request: CreateServiceRequest = {
            finalization_state: finalizationState,
            name: formFieldsState.name!,
            description: formFieldsState.description!,
            vendor_service_manager_user_id: formFieldsState.thirdPartyServiceManagerUserId!,
            assessment_due_date: props.service?.assessment_workflow_setup?.due_date,
            delegates: formFieldsState.delegates?.map((user) => user.cognito_subject),
            irq_target_completion_date: irqTargetCompletionDate ? jsDateToIso8601(irqTargetCompletionDate) : undefined,
            responsible_organization: formFieldsState.responsibleOrganization,
            tags: tags,
            vendor_contacts: cloneDeep(formFieldsState.third_party_contacts), // cloneDeep() is used so that we can set empty strings to undefined in the next step without modifying the original state, which would throw a React error about switching from controlled to uncontrolled components.
        };

        // Discard any Third-Party contacts where all attributes are "empty."
        // This is necessary because the API will save empty Third-Party contacts to the database (as {}) and then empty Third-Party contact "rows" will persist in the UI.
        request.vendor_contacts = request.vendor_contacts?.filter((thirdPartyContact) => {
            return Object.values(thirdPartyContact).some((value) => {
                if (typeof value === 'string' && value.trim().length === 0) {
                    return false;
                }
                return true;
            });
        });

        // Set any attributes of Third-Party contacts that are "empty" strings to undefined before submitting the request.
        // This is necessary because the API will return an error if email_address is an empty string. The other attributes are included simply for completeness.
        request.vendor_contacts?.forEach((thirdPartyContact) => {
            if ((thirdPartyContact.email_address?.trim().length ?? 0) === 0) {
                thirdPartyContact.email_address = undefined;
            }
            if ((thirdPartyContact.name?.trim().length ?? 0) === 0) {
                thirdPartyContact.name = undefined;
            }
            if ((thirdPartyContact.phone_number?.trim().length ?? 0) === 0) {
                thirdPartyContact.phone_number = undefined;
            }
            if ((thirdPartyContact.additional_information?.trim().length ?? 0) === 0) {
                thirdPartyContact.additional_information = undefined;
            }
        });

        return request;
    };

    const buildUpdateRequest = (): UpdateServiceRequest => {
        const request: UpdateServiceRequest = {
            name: formFieldsState.name!,
            description: formFieldsState.description!,
            vendor_service_manager_user_id: formFieldsState.thirdPartyServiceManagerUserId!,
            assessment_due_date: props.service?.assessment_workflow_setup?.due_date,
            delegates: formFieldsState.delegates?.map((user) => user.cognito_subject),
            irq_target_completion_date: props.service?.assessment_workflow_setup?.irq_target_completion_date,
            responsible_organization: formFieldsState.responsibleOrganization,
            tags: tags,
            vendor_contacts: cloneDeep(formFieldsState.third_party_contacts), // cloneDeep() is used so that we can set empty strings to undefined in the next step without modifying the original state, which would throw a React error about switching from controlled to uncontrolled components.
        };

        // Discard any Third-Party contacts where all attributes are "empty."
        // This is necessary because the API will save empty Third-Party contacts to the database (as {}) and then empty Third-Party contact "rows" will persist in the UI.
        request.vendor_contacts = request.vendor_contacts?.filter((thirdPartyContact) => {
            return Object.values(thirdPartyContact).some((value) => {
                if (typeof value === 'string' && value.trim().length === 0) {
                    return false;
                }
                return true;
            });
        });

        // Set any attributes of Third-Party contacts that are "empty" strings to undefined before submitting the request.
        // This is necessary because the API will return an error if email_address is an empty string. The other attributes are included simply for completeness.
        request.vendor_contacts?.forEach((thirdPartyContact) => {
            if ((thirdPartyContact.email_address?.trim().length ?? 0) === 0) {
                thirdPartyContact.email_address = undefined;
            }
            if ((thirdPartyContact.name?.trim().length ?? 0) === 0) {
                thirdPartyContact.name = undefined;
            }
            if ((thirdPartyContact.phone_number?.trim().length ?? 0) === 0) {
                thirdPartyContact.phone_number = undefined;
            }
            if ((thirdPartyContact.additional_information?.trim().length ?? 0) === 0) {
                thirdPartyContact.additional_information = undefined;
            }
        });

        return request;
    };

    // TODO: Some of this wording doesn't match up with the form field labels the user sees.
    const validateForm = (): void => {
        if (!formFieldsState.thirdPartyId) {
            throw new ValidationError('A service must be assigned to a third party.');
        }
        if (!formFieldsState.name) {
            throw new ValidationError('Service name is required.');
        }
        if (!formFieldsState.thirdPartyServiceManagerUserId) {
            throw new ValidationError('Service manager is required.');
        }
        if (!formFieldsState.description) {
            throw new ValidationError('Service description is required.');
        }
        formFieldsState.third_party_contacts.forEach((third_party_contact) => {
            if (third_party_contact.email_address && !validateEmail(third_party_contact.email_address)) {
                throw new ValidationError('Invalid email address.');
            }
        });
    };

    /** Handles error responses from the API. */
    const handleRequestError = (error: Error): void => {
        setRequestAndModalState({ requestState: 'failure', errorMessage: error.message, showToast: true });
    };

    /** Handles changes made to general text fields. */
    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        setFormFieldsState({ ...formFieldsState, [event.currentTarget.name]: event.currentTarget.value });
    };

    /** Handles changes made to general drop-down (select) fields. */
    const handleSelectChange = (value: ChangeEventType, formFieldId: string): void => {
        setFormFieldsState({ ...formFieldsState, [formFieldId]: value });
    };

    /** Handles changes made to the Third-Party Service Manager drop-down (select) field. */
    const handleSelectUserChange = (user: UserResponse | undefined, formFieldId: string): void => {
        if (user === undefined) {
            setServiceManager(undefined);
            setFormFieldsState({ ...formFieldsState, [formFieldId]: undefined });
        } else {
            setServiceManager(user);
            setFormFieldsState({ ...formFieldsState, [formFieldId]: user.cognito_subject });
        }
    };

    /** Handles changes made to the Third-Party Service Delegates drop-down (select) field. */
    const handleSelectUsersChange = (users: UserResponse[] | undefined, formFieldId: string): void => {
        setFormFieldsState({ ...formFieldsState, [formFieldId]: users });
    };

    /** Adds the fields (empty) for a new Third-Party contact. */
    const handleAddThirdPartyContact = (): void => {
        const thirdPartyContacts = [...formFieldsState.third_party_contacts];
        thirdPartyContacts.push({ name: '', email_address: '', phone_number: '', additional_information: '' });
        setFormFieldsState({ ...formFieldsState, third_party_contacts: thirdPartyContacts });
    };

    /** Deletes an existing Third-Party contact. */
    const handleDeleteThirdPartyContact = (index: number): void => {
        const thirdPartyContacts = [...formFieldsState.third_party_contacts];
        thirdPartyContacts.splice(index, 1);
        setFormFieldsState({ ...formFieldsState, third_party_contacts: thirdPartyContacts });
    };

    /** Handles changes made to an existing Third-Party contact. */
    const handleThirdPartyContactChange = (index: number, attribute: 'name' | 'email_address' | 'phone_number' | 'additional_information', value: string): void => {
        const thirdPartyContacts = [...formFieldsState.third_party_contacts];
        thirdPartyContacts[index] = {
            ...thirdPartyContacts[index],
            [attribute]: value,
        };
        setFormFieldsState({ ...formFieldsState, third_party_contacts: thirdPartyContacts });
    };

    const saveButtons = (() => {
        if (props.userHasFullTprmAccess && props.service === undefined) {
            return (
                <>
                    <Button variant="primary" fontAwesomeImage={ICON_SAVE} isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === 'createAsDraft'} loadingText="Saving..." onClick={() => createThirdPartyService(FinalizationState.DRAFT_IN_PROGRESS, 'createAsDraft')}>
                        Save as draft
                    </Button>
                    <Button
                        variant="primary"
                        fontAwesomeImage={faCheck}
                        isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === 'createAndSubmit'}
                        loadingText="Saving..."
                        onClick={() => {
                            try {
                                validateForm();
                                setRequestAndModalState({ requestState: 'idle', displayedModal: 'confirmFinalizeNewService' });
                            } catch (err) {
                                handleRequestError(err);
                            }
                        }}
                    >
                        Save and finalize
                    </Button>
                </>
            );
        } else {
            const action = props.service ? 'edit' : 'createAsDraft';

            return (
                <Button
                    variant="primary"
                    fontAwesomeImage={ICON_SAVE}
                    isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === action}
                    loadingText="Saving..."
                    onClick={() => {
                        if (props.service) {
                            updateThirdPartyService();
                        } else {
                            createThirdPartyService(FinalizationState.DRAFT_IN_PROGRESS, 'createAsDraft');
                        }
                    }}
                >
                    {props.userHasFullTprmAccess ? 'Save' : 'Save as draft'}
                </Button>
            );
        }
    })();

    const toast: JSX.Element = (() => {
        switch (requestAndModalState.requestState) {
            case 'idle':
            case 'inProgress':
                return <></>;
            case 'failure':
                return requestAndModalState.showToast ? <TextToast variant="failure" clearToast={() => setRequestAndModalState({ ...requestAndModalState, showToast: false })} text={requestAndModalState.errorMessage} /> : <></>;
            case 'success':
                switch (requestAndModalState.completedAction) {
                    case 'createAsDraft':
                    case 'createAndSubmit':
                    case 'edit':
                        return requestAndModalState.showToast ? <LinkButtonToast variant="success" clearToast={() => setRequestAndModalState({ ...requestAndModalState, showToast: false })} linkButtonText="View third parties and third-party services" linkButtonTo={generatePath(THIRD_PARTIES_AND_SERVICES_LISTING)} text="Third-party service saved." /> : <></>;
                    case 'createAndFinalize':
                    case 'delete':
                        return <></>;
                }
        }
    })();

    const headerTitle = props.service ? 'Manage Third-Party Service' : 'Create Third-Party Service';

    return (
        <>
            {(requestAndModalState.requestState === 'idle' || requestAndModalState.requestState === 'success') && requestAndModalState.displayedModal === 'confirmDelete' && props.serviceExists && (
                <ConfirmationModal
                    operationType="delete"
                    headerText="Delete Third-Party Service"
                    areYouSureText={`Are you sure you want to delete "${props.service.name}"?`}
                    performOperation={async () => {
                        await props.tprmApi.deleteService(props.service.vendor_id, props.service.id);
                        setRequestAndModalState({ requestState: 'success', completedAction: 'delete', displayedModal: 'confirmDelete' });
                        return 'Third-party service deleted.';
                    }}
                    hideModal={() => setRequestAndModalState({ ...requestAndModalState, displayedModal: undefined })}
                >
                    <ul className={styles.deleteConfirmationList}>
                        <li>
                            <Text>The in-progress assessment workflow (inherent risk questionnaire, third-party questionnaire, control assessment, and final review) for the third-party service will be deleted.</Text>
                        </li>
                        <li>
                            <Text>All historical assessment workflows for the third-party service will be deleted.</Text>
                        </li>
                        {props.userHasFullTprmAccess && props.service.assessment_workflow_setup.common_assessment_children.length > 0 && (
                            <>
                                <li>
                                    <Text>
                                        <b>This third-party service is providing a common assessment.</b> If you delete it, new third-party questionnaires will be generated for the following third-party services (based on their inherent risk scores):
                                    </Text>
                                </li>
                                <ul>
                                    <Text>
                                        {(props.service as ThirdPartyService).assessment_workflow_setup.common_assessment_children.map((child) => {
                                            return <li key={child.id}>{child.name}</li>;
                                        })}
                                    </Text>
                                </ul>
                            </>
                        )}
                    </ul>
                </ConfirmationModal>
            )}
            {(requestAndModalState.requestState === 'idle' || requestAndModalState.requestState === 'success') && requestAndModalState.displayedModal === 'confirmFinalizeNewService' && props.userHasFullTprmAccess && selectedThirdParty && formFieldsState.name && (
                <FinalizeThirdPartyServiceModal
                    onRequestCompleted={async () =>
                        setRequestAndModalState({
                            requestState: 'success',
                            completedAction: 'createAndFinalize',
                            displayedModal: 'confirmFinalizeNewService',
                        })
                    }
                    getCreateRequest={(irqTargetCompletionDate?: Date) => buildCreateRequest(FinalizationState.FINALIZED, irqTargetCompletionDate)}
                    action="finalizeNewService"
                    hideModal={() => setRequestAndModalState({ ...requestAndModalState, displayedModal: undefined })}
                    irqTargetCompletionSetting={props.irqTargetCompletionSetting}
                    serviceName={formFieldsState.name}
                    thirdParty={selectedThirdParty}
                    tprmApi={props.tprmApi}
                />
            )}
            {toast}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(THIRD_PARTIES_AND_SERVICES_LISTING)}>Third Parties and Services</BreadcrumbLink>
                        <BreadcrumbText>{headerTitle}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={headerTitle}
                body={[
                    {
                        content: (
                            <Form noValidate>
                                <fieldset disabled={disabled}>
                                    {props.service ? <Text variant="Header2">Manage Third-Party Service</Text> : <Text variant="Header2">Create Third-Party Service</Text>}
                                    <div className={styles.formFieldGroup}>
                                        <div className={styles.formFieldContainer}>
                                            <FormFieldSelect options={createThirdPartyOptions()} handleChange={handleSelectChange} formFieldId="thirdPartyId" formFieldLabel="Third Party" required selectedOption={formFieldsState.thirdPartyId} disabled={props.service !== undefined} />
                                        </div>
                                        <div className={styles.formFieldContainer}>
                                            <FormFieldText formFieldType="text" handleChange={handleChange} formFieldId="name" formFieldLabel="Name" required tooltip={TPRM_THIRD_PARTY_SERVICE_NAME} value={formFieldsState.name || ''} />
                                        </div>
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldText formFieldType="text" handleChange={handleChange} formFieldId="responsibleOrganization" formFieldLabel="Organization Responsible for Third-Party Relationship" tooltip={TPRM_THIRD_PARTY_SERVICE_RESPONSIBLE_ORGANIZATION} value={formFieldsState.responsibleOrganization || ''} />
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldUserSelect rolesRestriction={TPRM_ROLES} onUserSelected={handleSelectUserChange} formFieldId="thirdPartyServiceManagerUserId" selectedUser={serviceManager} formFieldLabel="Manager" tooltip={TPRM_THIRD_PARTY_SERVICE_MANAGER} required />
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldUserMultiSelect rolesRestriction={TPRM_ROLES} onUsersSelected={handleSelectUsersChange} formFieldId="delegates" selectedUsers={formFieldsState.delegates} formFieldLabel="Delegates" tooltip={TPRM_THIRD_PARTY_SERVICE_DELEGATES} />
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldTextArea handleChange={handleChange} formFieldId="description" formFieldLabel="Description" rows={5} required tooltip={TPRM_THIRD_PARTY_SERVICE_DESCRIPTION} value={formFieldsState.description || ''} />
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldMultiOptionSelect
                                            defaultSelectedOptions={cachedData
                                                .getTagCategoriesGroupedOptions()
                                                .map((group) => group.options)
                                                .flat()
                                                .filter((option) => tags.includes(option.value as string))}
                                            formFieldLabel="Tags"
                                            formFieldId="tags"
                                            handleChange={(value: GroupOptionType[]) => setTags(value.map((tag) => tag.value as string))}
                                            options={cachedData.getTagCategoriesGroupedOptions()}
                                            accessibilityLabel="Tags"
                                        />
                                    </div>
                                    <Text variant="Header3" color="darkGray">
                                        Third-Party Service Contact Information
                                    </Text>
                                    {formFieldsState.third_party_contacts.map((thirdPartyContact, index) => (
                                        <Fragment key={index}>
                                            <div className={styles.formFieldGroup}>
                                                <div className={styles.formFieldContainer}>
                                                    <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'name', event.currentTarget.value)} formFieldId={`thirdPartyContactName${index}`} formFieldLabel={`Name`} tooltip="The name of the contact at this third party for this service." value={thirdPartyContact.name || ''} />
                                                </div>
                                                <div className={styles.formFieldContainer}>
                                                    <FormFieldText formFieldType="email" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'email_address', event.currentTarget.value)} formFieldId={`thirdPartyContactEmailAddress${index}`} formFieldLabel={`Email Address`} tooltip="The email address of the contact at this third party for this service." value={thirdPartyContact.email_address || ''} />
                                                </div>
                                                <div className={styles.formFieldContainer}>
                                                    <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'phone_number', event.currentTarget.value)} formFieldId={`thirdPartyContactPhoneNumber${index}`} formFieldLabel={`Phone Number`} tooltip="The phone number of the contact at this third party for this service." value={thirdPartyContact.phone_number || ''} />
                                                </div>
                                                <div className={styles.trashIconContainer}>
                                                    <IconButton aria-label={`delete third-party contact ${index}`} onClick={() => handleDeleteThirdPartyContact(index)} fontAwesomeImage={ICON_DELETE_REMOVE} />
                                                </div>
                                            </div>
                                            <div className={styles.formFieldContainer}>
                                                <FormFieldTextArea handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'additional_information', event.currentTarget.value)} formFieldId={`thirdPartyContactAdditionalInformation${index}`} formFieldLabel={`Additional Information`} tooltip="Any additional information for the contact at this third party." value={thirdPartyContact.additional_information || ''} />
                                            </div>
                                        </Fragment>
                                    ))}
                                    <div className={styles.formFieldContainerBottom}>
                                        <Button variant="linkText" size="lg" onClick={handleAddThirdPartyContact}>
                                            {'+ Add Contact'}
                                        </Button>
                                    </div>
                                    <div className={'modalFormButtonContainer'}>
                                        <>
                                            {props.service && (
                                                <div className={styles.deleteButton}>
                                                    <Button fontAwesomeImage={ICON_DELETE_REMOVE} onClick={() => setRequestAndModalState({ requestState: 'idle', displayedModal: 'confirmDelete' })} variant="danger" isLoading={false} loadingText="Deleting...">
                                                        Delete
                                                    </Button>
                                                </div>
                                            )}
                                            {saveButtons}
                                        </>
                                    </div>
                                </fieldset>
                            </Form>
                        ),
                    },
                ]}
            />
        </>
    );
};
